import styled from 'styled-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createColumnHelper } from '@tanstack/react-table';

import {
  Button,
  Chip,
  ColorsV2,
  Flex,
  Icon,
  IconButton,
  Table,
  TableContainer,
  TableExpandCell,
  Text,
  getToken,
  spaces,
  useThemeColors,
} from '@electricjs/arc';
import type { IconNames } from '@electricjs/arc';

import { useGetApplicationsForGroups } from '@/components/People/EmployeeAppsByGroup/useGetApplicationsForGroups';
import { Group, GroupMembershipType } from '@/types/groups';
import { useGetOrganizationApplicationsQuery } from '@/redux/slices/organizationApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { CenteredSpinner } from '@common';
import ApplicationIcon from '@/components/Applications/ApplicationIcon';
import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import { AutomationStatusChip } from '@/components/Applications/ProvisioningAppsTable/AutomationStatusChip';
import {
  ManagedAppRow,
  ExternalAppRow,
  ExcludedAppRow,
} from '@/components/Applications/ProvisioningAppsTable/types';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { AddOnboardingAppModal } from './AddOnboardingAppModal';
import { OnboardingFormData } from './types';
import { Application } from '@/types/applications';
import { ManagedAppSettingsPanel } from './ManagedAppSettings/ManagedAppSettingsPanel';

type OnboardEmployeeModalAppsProps = {
  orgHasNoGroups: boolean;
  employeeGroups: Group[];
  onLoadingChange?: (isLoading: boolean) => void;
};

// Mirrors the icon treatment used for the read-only group chips in
// Applications.tsx: dynamic groups are automatically matched, HRIS-synced
// groups are locked to their source system. Plain static groups get no icon.
const getGroupChipIcon = (group: Group): IconNames | undefined => {
  if (group.membershipType === GroupMembershipType.Dynamic) return 'bolt';
  if (group.hrisGroupIntegration) return 'lock';
  return undefined;
};

const TextCenteredDiv = styled.div`
  text-align: center;
  margin: 2rem;
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 3px;
  background-color: ${getToken(ColorsV2.GRAY_MEDIUM)};
`;

const SectionLabelRow = styled(Flex)`
  background-color: ${getToken(ColorsV2.GRAY_LIGHTEST)};
  width: 100%;
  padding: 0.8rem 2rem;
  align-items: center;
  justify-content: space-between;
`;

const Error = ({ title, message }: { title: string; message?: string }) => {
  const { t } = useTranslation('common');
  const defaultMessage = t(
    'onboardings.onboardEmployeeModalApps.defaultMessage'
  );

  return (
    <TextCenteredDiv>
      <Text variant="heading-2" mb={spaces.space_xs}>
        {title}
      </Text>
      <Text variant="body-2">{message ?? defaultMessage}</Text>
    </TextCenteredDiv>
  );
};

const managedColumnHelper = createColumnHelper<ManagedAppRow>();
const externalColumnHelper = createColumnHelper<ExternalAppRow>();
const excludedColumnHelper = createColumnHelper<ExcludedAppRow>();

const OnboardEmployeeModalApps = ({
  orgHasNoGroups,
  employeeGroups,
  onLoadingChange,
}: OnboardEmployeeModalAppsProps) => {
  const { t } = useTranslation('common');
  const { ithOnboardingAppsTable } = useFlags();
  const [infoBackground, infoText] = useThemeColors([
    ColorsV2.INFO_LIGHTEST,
    ColorsV2.INFO_DARKEST,
  ]);
  const organizationId = useGetOrganizationId();
  const { openModal, closeModal } = useGlobalUI();
  // All groups this employee is being onboarded into (not just the groups
  // that provision a given app) -- a group can carry a settings override for
  // an app even when it isn't the group granting access to it.
  const employeeGroupIds = useMemo(
    () => employeeGroups.map(group => group.id),
    [employeeGroups]
  );

  const { watch, setValue, getValues } = useFormContext<OnboardingFormData>();
  const additionalAppsRaw = watch('additionalApps');
  const additionalApps = useMemo(
    () => additionalAppsRaw ?? [],
    [additionalAppsRaw]
  );
  const excludedAppsRaw = watch('excludedApps');
  const excludedApps = useMemo(() => excludedAppsRaw ?? [], [excludedAppsRaw]);
  const excludedAppIds = useMemo(
    () => new Set(excludedApps.map(a => a.id)),
    [excludedApps]
  );

  const {
    data: appsByGroup,
    isLoading: isLoadingApps,
    isError: isErrorApps,
  } = useGetApplicationsForGroups(employeeGroups);

  const {
    data: orgApplicationsData,
    isLoading: isLoadingOrgApps,
    isError: isErrorOrgApps,
  } = useGetOrganizationApplicationsQuery({ organizationId, limit: 1000 });

  const isLoading = isLoadingApps || isLoadingOrgApps;
  const isError = isErrorApps || isErrorOrgApps;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const orgAppMap = useMemo(() => {
    return new Map(
      (orgApplicationsData?.applications ?? []).map(a => [a.application.id, a])
    );
  }, [orgApplicationsData]);

  const { provisionedRows, externalRows, excludedRows, groupAppIds } =
    useMemo(() => {
      const appGroupsMap = new Map<
        string,
        { appName: string; appIcon?: string; appSlug: string; groups: Group[] }
      >();

      (appsByGroup ?? []).forEach(({ group, applications }) => {
        applications.forEach(app => {
          const existing = appGroupsMap.get(app.id);
          if (existing) {
            existing.groups.push(group);
          } else {
            appGroupsMap.set(app.id, {
              appName: app.name,
              appIcon: app.icon,
              appSlug: app.slug,
              groups: [group],
            });
          }
        });
      });

      const provisioned: ManagedAppRow[] = [];
      const external: ExternalAppRow[] = [];
      const excluded: ExcludedAppRow[] = [];

      appGroupsMap.forEach(({ appName, appIcon, appSlug, groups }, appId) => {
        const orgApp = orgAppMap.get(appId);
        const isDisabled = orgApp?.disableCreateAccountRequests ?? false;

        if (excludedAppIds.has(appId)) {
          excluded.push({ appId, appName, appIcon, groups });
        } else if (isDisabled) {
          external.push({ appId, appName, appIcon, groups });
        } else {
          const isAutomated = Boolean(
            orgApp?.integrationConnected && orgApp?.onboardingAutomationOn
          );
          provisioned.push({
            appId,
            appName,
            appIcon,
            appSlug,
            groups,
            isAutomated,
          });
        }
      });

      return {
        provisionedRows: provisioned,
        externalRows: external,
        excludedRows: excluded,
        groupAppIds: new Set(appGroupsMap.keys()),
      };
    }, [appsByGroup, orgAppMap, excludedAppIds]);

  // When groups change, clean up stale additionalApps and excludedApps
  useEffect(() => {
    if (!ithOnboardingAppsTable) return;
    const currentAdditional = getValues('additionalApps') ?? [];
    const filteredAdditional = currentAdditional.filter(
      (a: Application) => !groupAppIds.has(a.id)
    );
    if (filteredAdditional.length !== currentAdditional.length) {
      setValue('additionalApps', filteredAdditional);
    }
    // Remove excluded apps that are no longer in any group
    const currentExcluded = getValues('excludedApps') ?? [];
    const filteredExcluded = currentExcluded.filter((a: Application) =>
      groupAppIds.has(a.id)
    );
    if (filteredExcluded.length !== currentExcluded.length) {
      setValue('excludedApps', filteredExcluded);
    }
    // getValues is stable; groupAppIds is the intended trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupAppIds, ithOnboardingAppsTable]);

  const allProvisionedRows: ManagedAppRow[] = useMemo(
    () => [
      ...provisionedRows,
      ...(ithOnboardingAppsTable
        ? additionalApps.map(app => {
            const orgApp = orgAppMap.get(app.id);
            const isAutomated = Boolean(
              orgApp?.integrationConnected && orgApp?.onboardingAutomationOn
            );
            return {
              appId: app.id,
              appName: app.name,
              appIcon: app.icon,
              appSlug: app.slug,
              isAutomated,
              groups: [] as Group[],
              isManuallyAdded: true as const,
            };
          })
        : []),
    ],
    // orgAppMap is intentionally omitted: provisionedRows already depends on it,
    // so allProvisionedRows re-runs whenever orgAppMap changes via that indirect chain
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provisionedRows, additionalApps, ithOnboardingAppsTable]
  );

  const pickerExcludedAppIds = useMemo(() => {
    return new Set([...groupAppIds, ...additionalApps.map(a => a.id)]);
  }, [groupAppIds, additionalApps]);

  const handleExcludeApp = useCallback(
    (appId: string) => {
      const orgApp = orgAppMap.get(appId);
      if (!orgApp) return;
      const current = getValues('excludedApps') ?? [];
      if (current.some(a => a.id === appId)) return;
      setValue('excludedApps', [...current, orgApp.application]);
    },
    [orgAppMap, getValues, setValue]
  );

  const handleRestoreApp = useCallback(
    (appId: string) => {
      const current = getValues('excludedApps') ?? [];
      setValue(
        'excludedApps',
        current.filter(a => a.id !== appId)
      );
    },
    [getValues, setValue]
  );

  const handleAddApps = (apps: Application[]) => {
    const newApps = apps.filter(a => !pickerExcludedAppIds.has(a.id));
    setValue('additionalApps', [...additionalApps, ...newApps]);
  };

  const handleRemoveApp = useCallback(
    (appId: string) => {
      setValue(
        'additionalApps',
        additionalApps.filter(a => a.id !== appId)
      );
    },
    [additionalApps, setValue]
  );

  const openAddAppModal = () => {
    openModal(
      <AddOnboardingAppModal
        closeModal={closeModal}
        onAdd={handleAddApps}
        excludedAppIds={pickerExcludedAppIds}
      />
    );
  };

  const handleRemoveManagedRow = useCallback(
    (row: ManagedAppRow) => {
      if (row.isManuallyAdded) {
        handleRemoveApp(row.appId);
      } else {
        handleExcludeApp(row.appId);
      }
    },
    [handleRemoveApp, handleExcludeApp]
  );

  const isManagedRowRemovable = useCallback(
    (row: ManagedAppRow) =>
      Boolean(row.isManuallyAdded || orgAppMap.has(row.appId)),
    [orgAppMap]
  );

  const managedColumns = useMemo(
    () => [
      ...(ithOnboardingAppsTable
        ? [
            managedColumnHelper.display({
              id: 'expand',
              header: '',
              cell: ({ row }) => <TableExpandCell row={row} />,
              meta: {
                flex: '0 0 2.4rem',
                minWidth: '2.4rem',
                padding: '0.4rem',
              },
            }),
          ]
        : []),
      managedColumnHelper.display({
        id: 'application',
        header: t('onboardings.onboardEmployeeModalApps.columnApplication'),
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <Flex vAlignContent="center" gap="1rem">
              <ApplicationIcon
                name={row.original.appName}
                icon={row.original.appIcon}
                size="3rem"
              />
              <Text variant="body-2">{row.original.appName}</Text>
            </Flex>
          </TableCellFlexContainer>
        ),
        meta: { flex: '3' },
      }),
      managedColumnHelper.display({
        id: 'automationStatus',
        header: t('onboardings.onboardEmployeeModalApps.columnProvisionedBy'),
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <AutomationStatusChip
              id={`automation-status-${row.original.appId}`}
              isAutomated={row.original.isAutomated}
            />
          </TableCellFlexContainer>
        ),
        meta: { flex: '2.5' },
      }),
      managedColumnHelper.display({
        id: 'groups',
        header: t('onboardings.onboardEmployeeModalApps.columnGroups'),
        cell: ({ row }) => (
          <TableCellFlexContainer gap="0.5rem" flexWrap="wrap">
            {row.original.isManuallyAdded ? (
              <Chip
                id={`manually-added-chip-${row.original.appId}`}
                variant="small"
                backgroundColor={infoBackground}>
                <Text variant="legal" color={infoText}>
                  {t('onboardings.onboardEmployeeModalApps.manuallyAdded')}
                </Text>
              </Chip>
            ) : (
              row.original.groups.map(group => (
                <Chip
                  key={group.id}
                  id={`managed-group-chip-${row.original.appId}-${group.id}`}
                  variant="small"
                  backgroundColor={ColorsV2.GRAY_LIGHTEST}
                  icon={getGroupChipIcon(group)}>
                  <Text variant="legal" title={group.name} truncateAt="12rem">
                    {group.name}
                  </Text>
                </Chip>
              ))
            )}
          </TableCellFlexContainer>
        ),
        meta: { flex: '2.5' },
      }),
      ...(ithOnboardingAppsTable
        ? [
            managedColumnHelper.display({
              id: 'remove',
              header: '',
              cell: ({ row }) => (
                <TableCellFlexContainer justifyContent="flex-end">
                  {isManagedRowRemovable(row.original) && (
                    <IconButton
                      id={`remove-app-${row.original.appId}`}
                      icon="close"
                      alt={t(
                        row.original.isManuallyAdded
                          ? 'onboardings.onboardEmployeeModalApps.removeAddedApp'
                          : 'onboardings.onboardEmployeeModalApps.excludeApp'
                      )}
                      onClick={e => {
                        // Rows are clickable (expandOnRowClick) to expand the
                        // Access & Settings panel -- without this, removing
                        // an app would also toggle that row's expanded state.
                        e.stopPropagation();
                        handleRemoveManagedRow(row.original);
                      }}
                    />
                  )}
                </TableCellFlexContainer>
              ),
              meta: { flex: '0 0 9rem' },
            }),
          ]
        : []),
    ],
    [
      t,
      handleRemoveManagedRow,
      isManagedRowRemovable,
      infoBackground,
      infoText,
      ithOnboardingAppsTable,
    ]
  );

  const externalColumns = useMemo(
    () => [
      externalColumnHelper.display({
        id: 'application',
        header: t('onboardings.onboardEmployeeModalApps.columnApplication'),
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <Flex vAlignContent="center" gap="1rem">
              <ApplicationIcon
                name={row.original.appName}
                icon={row.original.appIcon}
                size="3rem"
              />
              <Text variant="body-2">{row.original.appName}</Text>
            </Flex>
          </TableCellFlexContainer>
        ),
        meta: { flex: '3' },
      }),
      externalColumnHelper.display({
        id: 'spacer',
        header: '',
        cell: () => null,
        meta: { flex: '2.5' },
      }),
      externalColumnHelper.display({
        id: 'groups',
        header: t('onboardings.onboardEmployeeModalApps.columnGroups'),
        cell: ({ row }) => (
          <TableCellFlexContainer gap="0.5rem" flexWrap="wrap">
            {row.original.groups.map(group => (
              <Chip
                key={group.id}
                id={`external-group-chip-${row.original.appId}-${group.id}`}
                variant="small"
                backgroundColor={ColorsV2.GRAY_LIGHTEST}
                icon={getGroupChipIcon(group)}>
                <Text variant="legal" title={group.name} truncateAt="12rem">
                  {group.name}
                </Text>
              </Chip>
            ))}
          </TableCellFlexContainer>
        ),
        meta: { flex: '2.5' },
      }),
      ...(ithOnboardingAppsTable
        ? [
            externalColumnHelper.display({
              id: 'remove',
              header: '',
              cell: ({ row }) => (
                <TableCellFlexContainer justifyContent="flex-end">
                  {orgAppMap.has(row.original.appId) && (
                    <IconButton
                      id={`exclude-external-app-${row.original.appId}`}
                      icon="close"
                      alt={t('onboardings.onboardEmployeeModalApps.excludeApp')}
                      onClick={() => handleExcludeApp(row.original.appId)}
                    />
                  )}
                </TableCellFlexContainer>
              ),
              meta: { flex: '0 0 9rem' },
            }),
          ]
        : []),
    ],
    [t, ithOnboardingAppsTable, orgAppMap, handleExcludeApp]
  );

  const excludedColumns = useMemo(
    () => [
      excludedColumnHelper.display({
        id: 'application',
        header: t('onboardings.onboardEmployeeModalApps.columnApplication'),
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <Flex vAlignContent="center" gap="1rem">
              <ApplicationIcon
                name={row.original.appName}
                icon={row.original.appIcon}
                size="3rem"
              />
              <Text variant="body-2">{row.original.appName}</Text>
            </Flex>
          </TableCellFlexContainer>
        ),
        meta: { flex: '3' },
      }),
      excludedColumnHelper.display({
        id: 'spacer',
        header: '',
        cell: () => null,
        meta: { flex: '2.5' },
      }),
      excludedColumnHelper.display({
        id: 'excludedFrom',
        header: t('onboardings.onboardEmployeeModalApps.columnExcludedFrom'),
        cell: ({ row }) => (
          <TableCellFlexContainer gap="0.5rem" flexWrap="wrap">
            {row.original.groups.map(group => (
              <Chip
                key={group.id}
                id={`excluded-group-chip-${row.original.appId}-${group.id}`}
                variant="small"
                backgroundColor={ColorsV2.GRAY_LIGHTEST}
                icon={getGroupChipIcon(group)}>
                <Text variant="legal" truncateAt="12rem">
                  {group.name}
                </Text>
              </Chip>
            ))}
          </TableCellFlexContainer>
        ),
        meta: { flex: '2.5' },
      }),
      excludedColumnHelper.display({
        id: 'restore',
        header: '',
        cell: ({ row }) => (
          <TableCellFlexContainer justifyContent="flex-end">
            <Button
              id={`restore-excluded-app-${row.original.appId}`}
              variant="text"
              onClick={() => handleRestoreApp(row.original.appId)}>
              {t('onboardings.onboardEmployeeModalApps.restoreApp')}
            </Button>
          </TableCellFlexContainer>
        ),
        meta: { flex: '0 0 9rem' },
      }),
    ],
    [t, handleRestoreApp]
  );

  const provisionedSectionVisible =
    allProvisionedRows.length > 0 || !!ithOnboardingAppsTable;
  const appCount =
    allProvisionedRows.length + externalRows.length + excludedRows.length;

  return (
    <Flex flexDirection="column" width="100%">
      {orgHasNoGroups && (
        <Error
          title={t('onboardings.onboardEmployeeModalApps.noGroupsTitle')}
        />
      )}
      {!orgHasNoGroups && employeeGroups.length === 0 && (
        <Error
          title={t('onboardings.onboardEmployeeModalApps.assignGroupTitle')}
        />
      )}
      {!ithOnboardingAppsTable &&
        !isLoading &&
        !isError &&
        employeeGroups.length > 0 &&
        appCount === 0 && (
          <Error
            title={t(
              'onboardings.onboardEmployeeModalApps.noApplicationsTitle',
              {
                groupType:
                  employeeGroups.length > 1
                    ? t('onboardings.onboardEmployeeModalApps.theseGroups')
                    : t('onboardings.onboardEmployeeModalApps.thisGroup'),
              }
            )}
          />
        )}
      {isError && (
        <Error
          title={t('onboardings.onboardEmployeeModalApps.errorTitle')}
          message=""
        />
      )}

      {!isError &&
        (!!ithOnboardingAppsTable || employeeGroups.length > 0) &&
        (isLoading || appCount > 0 || !!ithOnboardingAppsTable) && (
          <TableContainer flexDirection="column" width="100%">
            {isLoading && <CenteredSpinner py="4.3rem" />}

            {!isLoading && !isError && (
              <>
                {provisionedSectionVisible && (
                  <SectionLabelRow>
                    <Text variant="label-large">
                      {t(
                        'onboardings.onboardEmployeeModalApps.provisionedSectionHeader',
                        { count: allProvisionedRows.length }
                      )}
                    </Text>
                    {ithOnboardingAppsTable && (
                      <Button
                        id="add-onboarding-app-button"
                        variant="outline"
                        onClick={openAddAppModal}>
                        <Flex alignItems="center" gap="0.6rem">
                          <Icon icon="plus" size="small" />
                          {t(
                            'onboardings.onboardEmployeeModalApps.addApplicationButton'
                          )}
                        </Flex>
                      </Button>
                    )}
                  </SectionLabelRow>
                )}
                {allProvisionedRows.length > 0 && (
                  <Table<ManagedAppRow>
                    id="onboarding-provisioned-apps-table"
                    columns={managedColumns}
                    data={allProvisionedRows}
                    loading={false}
                    emptyStateText=""
                    // Without this, TanStack keys expanded/row state by array
                    // index, so removing a row shifts which row appears
                    // expanded. appId is stable across additions/removals.
                    dataPropertyAsSelectionKey="appId"
                    // Only automated apps have provisioning settings worth
                    // previewing -- manually provisioned apps have nothing to
                    // show, so they don't get an expand affordance at all.
                    getRowCanExpand={row =>
                      !!ithOnboardingAppsTable && row.original.isAutomated
                    }
                    expandOnRowClick={!!ithOnboardingAppsTable}
                    renderSubComponent={
                      ithOnboardingAppsTable
                        ? ({ row }) => (
                            <ManagedAppSettingsPanel
                              applicationKey={row.original.appSlug}
                              groupIds={employeeGroupIds}
                            />
                          )
                        : undefined
                    }
                  />
                )}

                {externalRows.length > 0 && (
                  <>
                    {provisionedSectionVisible && <SectionDivider />}
                    <SectionLabelRow>
                      <Text variant="label-large">
                        {t(
                          'onboardings.onboardEmployeeModalApps.externalSectionHeader',
                          { count: externalRows.length }
                        )}
                      </Text>
                    </SectionLabelRow>
                    <Table
                      id="onboarding-external-apps-table"
                      columns={externalColumns}
                      data={externalRows}
                      loading={false}
                      emptyStateText=""
                    />
                  </>
                )}

                {ithOnboardingAppsTable && excludedRows.length > 0 && (
                  <>
                    <SectionDivider />
                    <SectionLabelRow>
                      <Text variant="label-large">
                        {t(
                          'onboardings.onboardEmployeeModalApps.excludedSectionHeader',
                          { count: excludedRows.length }
                        )}
                      </Text>
                    </SectionLabelRow>
                    <Table
                      id="onboarding-excluded-apps-table"
                      columns={excludedColumns}
                      data={excludedRows}
                      loading={false}
                      emptyStateText=""
                    />
                  </>
                )}
              </>
            )}
          </TableContainer>
        )}
    </Flex>
  );
};

export default OnboardEmployeeModalApps;
